import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, Plus, Trash2, Shield, Crown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("worker");
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null);

  const isOwner = currentUser?.role === "owner";

  const show = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/users`, { email: newEmail.trim().toLowerCase(), role: newRole }, { withCredentials: true });
      show("User added successfully");
      setNewEmail("");
      load();
    } catch (err) {
      show(err.response?.data?.detail || "Failed to add user", "error");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the team? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { withCredentials: true });
      show("User removed");
      load();
    } catch (err) {
      show(err.response?.data?.detail || "Failed to remove", "error");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-tab">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"
        }`}>{toast.msg}</div>
      )}

      {/* Add User */}
      {isOwner && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-slate-400" /> Add Team Member
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Gmail Address</Label>
                <Input
                  data-testid="add-user-email"
                  type="email"
                  placeholder="team.member@gmail.com"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="w-36">
                <Label className="text-xs text-slate-500 mb-1 block">Role</Label>
                <select
                  data-testid="add-user-role"
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="worker">Worker</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="add-user-btn"
                  type="submit"
                  disabled={adding || !newEmail.trim()}
                  className="bg-[#002147] hover:bg-[#001a3a] text-sm h-10 px-5"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </form>
            <p className="text-[10px] text-slate-400 mt-2">
              Only Gmail addresses are supported. Team members will need to sign in with Google.
            </p>
          </CardContent>
        </Card>
      )}

      {/* User List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-400" /> Authorized Team ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No team members yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div
                  key={u.email}
                  data-testid={`user-row-${u.email}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      u.role === "owner" ? "bg-[#D4AF37]/10" : "bg-slate-100"
                    }`}>
                      {u.role === "owner"
                        ? <Crown className="h-4 w-4 text-[#D4AF37]" />
                        : <Shield className="h-4 w-4 text-slate-400" />
                      }
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{u.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] capitalize ${
                            u.role === "owner" ? "border-[#D4AF37] text-[#D4AF37]" : "border-slate-300 text-slate-500"
                          }`}
                        >
                          {u.role}
                        </Badge>
                        {u.added_by !== "system" && (
                          <span className="text-[10px] text-slate-400">Added by {u.added_by}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isOwner && u.role !== "owner" && (
                    <button
                      onClick={() => handleRemove(u.email)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors shrink-0"
                      title="Remove user"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!isOwner && (
        <div className="text-center text-xs text-slate-400 py-4">
          Only the account owner can add or remove team members.
        </div>
      )}
    </div>
  );
}
